/**
 * Utility functions for creating standardized storage IDs across different storage services
 */

/**
 * Create a standardized document/storage ID for question data
 * This ID format is used consistently across LocalStorage, Firebase, and other storage services
 * Format: {tenantId}_{userId}_{testId}_{questionId}
 */
export function createQuestionDataId(
  tenantId: number | string,
  userId: number | string, 
  testId: string,
  questionId: string
): string {
  return `${tenantId}_${userId}_${testId}_${questionId}`;
}

/**
 * Parse a question data ID to extract its components
 */
export function parseQuestionDataId(id: string): {
  tenantId: string;
  userId: string;
  testId: string;
  questionId: string;
} | null {
  const parts = id.split('_');
  
  if (parts.length < 4) {
    return null;
  }
  
  // Handle cases where testId or questionId might contain underscores
  const [tenantId, userId, ...rest] = parts;
  
  // Find the last underscore that separates testId and questionId
  // Assuming questionId is typically a GUID or simple ID without underscores
  const lastUnderscoreIndex = rest.lastIndexOf('_');
  
  if (lastUnderscoreIndex === -1) {
    // No underscore found, treat entire rest as testId
    return {
      tenantId,
      userId,
      testId: rest.join('_'),
      questionId: ''
    };
  }
  
  const testId = rest.slice(0, lastUnderscoreIndex).join('_');
  const questionId = rest.slice(lastUnderscoreIndex + 1).join('_');
  
  return {
    tenantId,
    userId,
    testId,
    questionId
  };
}

/**
 * Check if a storage key is a question data key (for migration purposes)
 */
export function isQuestionDataKey(key: string): boolean {
  // Check for old format with prefix
  if (key.startsWith('questionData_')) {
    return true;
  }
  
  // Check for new format (4 or more parts separated by underscore)
  const parts = key.split('_');
  return parts.length >= 4 && !isNaN(Number(parts[0])) && !isNaN(Number(parts[1]));
}

/**
 * Migrate old key format to new format
 */
export function migrateQuestionDataKey(oldKey: string): string | null {
  if (!oldKey.startsWith('questionData_')) {
    return oldKey; // Already in new format
  }
  
  // Remove the prefix
  return oldKey.replace('questionData_', '');
}